import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Table from "@/components/Table/table";
import { MotifButton, MotifProgressLoader } from "@ey-xd/motif-react";
import { useGetColumnDefsManualDataSource } from "./manualDataSourceConst";
import ManualDataSourceUploader from "./ManualDataSourceUploader";
import {
  useDeleteManualDataSourceMutation,
  useLazyDownloadManualSourceDocumentQuery,
  useListManualDataSourceQuery,
} from "@/services/manualDataSource/ManualDataSourceReducer";
import { useCase } from "@/features/case/use-case";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { commonFormateName, havingAddPermission, havingDeletePermission } from "@/utility/common";
import {
  CREATE_BUTTON_VARIANT,
  GetCommonDeleteImgButton,
  GetCommonDownloadImgButton,
} from "@/components/Images/CommonImages";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import { useTrans } from "@/components/Functions/dateFunc";
import WarningModal from "@/components/Modal/WarningModal/WarningModal";
import NotificationGrid from "@/components/Notification/NotificationGrid";

export const ManualDataSource = () => {
  const parameter = useParams();
  const { t } = useTranslation();
  let tableLoadingData = customTableLoadingData;
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWarningModalDetails, setShowWarningModalDetails] = useState({
    show: false,
    message: "",
  });
  const [notificationDetails, setNotificationDetails] = useState<any>({
    show: false,
    type: "",
    message: "",
  });
  const [selectedManualDataSource, setSelectedManualDataSource] = useState<any>(
    {
      id: "",
      manualDataSourceName: "",
    }
  );
  const retainState = useSelector(
    (e: any) => e.retainState.manualDataListTabState
  );
  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseId: parameter?.caseId ?? "",
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [parameter?.caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;
  const { data, isFetching, isError, isLoading, refetch, status } =
    useListManualDataSourceQuery(paginationObj);
  const [
    deleteManulaDataSource,
    {isLoading: deletingManualDataSource },
  ] = useDeleteManualDataSourceMutation();
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "manualDataListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  const [manualDataSourceId, setManualDataSourceId] = useState("");

  const caseDara = useCase();
  const getColumnDefsManualDataSource = useGetColumnDefsManualDataSource(
    caseDara?.abbreviation,
    manualDataSourceId
  );
  const permissions = useSelector((state: any) => state.permissions);
  const { ManualDataSource } = permissions;
  const [showUploadNew, setShowUploadNew] = useState(false);
  //const [selectedDocument, setSelectedDocuement] = useState<any>({})
  const [downloadManualSourceDocument] =
    useLazyDownloadManualSourceDocumentQuery();
  const { caseShortName, abbreviation } = useSelector(
    (state: any) => state.case
  );

  useEffect(() => {
    if (showUploadNew === false) {
      refetch();
    }
    // eslint-disable-next-line
  }, [showUploadNew]);
  function TitleComplement() {
    return havingAddPermission(ManualDataSource) ? (
      <MotifButton
        onClick={handleUploadNew}
        size="small"
        type="submit"
        variant={CREATE_BUTTON_VARIANT}
      >
        {t("pages.distributions.manualDataSource.uploadManualData")}{" "}
      </MotifButton>
    ) : (
      <></>
    );
  }

  const customActionHeader = {
    editCellRenderer: function (params: any) {
      return (
        <>
          {
            <Link to={`./manual-data-source-edit/${params.data.id}`}>
              <span className="text-bold-underline">
                {params.data.manualDataSourceName}
              </span>
            </Link>
          }
        </>
      );
    },
    actionRenderer: function (props: any) {
      return (
        <>
          <GetCommonDownloadImgButton
            loading={props?.selectedDataSourceId === props?.data?.id}
            onClick={() => handleDownload(props.data)}
          ></GetCommonDownloadImgButton>
          {havingDeletePermission(ManualDataSource) && <GetCommonDeleteImgButton
            onClick={() => {
              setShowDeleteModal(true);
              setSelectedManualDataSource({
                id: props.data.id,
                manualDataSourceName: props.data.manualDataSourceName,
              });
            }}
          ></GetCommonDeleteImgButton>}
        </>
      );
    },
  };

  const handleUploadNew = () => {
    //setSelectedDocuement({})
    setShowUploadNew(true);
  };

  const handleDownload = (documentData: any) => {
    setManualDataSourceId(documentData.id);
    downloadManualSourceDocument({
      manualDataSourceRecordId: documentData.id,
      fileName:
        documentData?.manualDataSourceName && caseShortName
          ? commonFormateName(
              caseShortName,
              documentData?.manualDataSourceName,
              abbreviation,
              "ManualDataSource"
            )
          : "",
    })
      .then((response: any) => {
        setManualDataSourceId("");
      })
      .catch((error: any) => {
        setManualDataSourceId("");
      });
  };

  return (
    <>
      <div className="mar-b-20">
        {isLoading ? (
          <MotifProgressLoader show variant="default" className="mar-b-20" />
        ) : (
          <>
            {notificationDetails.show && (
              <NotificationGrid
                show={notificationDetails.show}
                type={notificationDetails.type}
                message={notificationDetails.message}
                onClose={() => {
                  setNotificationDetails({
                    show: false,
                    type: "",
                    message: "",
                  });
                }}
              />
            )}

            <Table
              columnTypes={{ ...retainState.sortedColumnTypes }}
              handlePaginationData={handlePaginationData}
              paginationObj={paginationObj}
              columnDefs={getColumnDefsManualDataSource}
              cellRenderers={data?.results && !isFetching ? data.results : []}
              TitleComplement={TitleComplement}
              title={"pages.distributions.manualDataSource.tableTitle"}
              description={
                "pages.distributions.manualDataSource.tableDescription"
              }
              caseRenderers={customActionHeader}
              pagination={false}
              overlayNoRowsTemplate={
                isFetching
                  ? t(tableLoadingData)
                  : isError
                  ? t("global.globalNotifications.wentWrong")
                  : t("global.globalNotifications.noRecords")
              }
            />
            <CustomPagination
              apiData={data}
              apiStatus={status}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!data?.hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "manualDataListTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "manualDataListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "manualDataListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          </>
        )}
      </div>
      <ConfirmationModal
        isVisible={showDeleteModal}
        setIsVisible={setShowDeleteModal}
        cancelBtnRejectHandler={() => {setSelectedManualDataSource({
          id: "",
          manualDataSourceName: "",
        }); setShowDeleteModal(false)}}
        cancelBtnApproveHandler={() => {
          deleteManulaDataSource({
            manualDataSourceId: selectedManualDataSource.id,
          }).then((res: any) => {
            if (res?.error && res?.error?.status === 400) {
              setShowWarningModalDetails({
                show: true,
                message:
                  res?.error?.data?.message ||
                  t("global.globalNotifications.wentWrong"),
              });
            } else if (res?.data) {
              setNotificationDetails({
                show: true,
                type: "success",
                message: t(
                  "pages.distributions.manualDataSource.deletedSuccessMessage",
                  {
                    deleteEntityName:
                      selectedManualDataSource.manualDataSourceName,
                  }
                ),
              });
              refetch()
            } else {
              setNotificationDetails({
                show: true,
                type: "error",
                message: t("global.globalNotifications.wentWrong"),
              });
            }
            setShowDeleteModal(false);
          }).catch(()=>{
            setNotificationDetails({
              show: true,
              type: "error",
              message: t("global.globalNotifications.wentWrong"),
            });
          });
        }}
        type={3}
        loading={deletingManualDataSource}
        showHeader={false}
        message={useTrans(
          t,
          t("pages.distributions.manualDataSource.confirmationMessage", {
            deleteEntityName: selectedManualDataSource.manualDataSourceName,
          }),
          "deleteEntityName"
        )}
      />

      <WarningModal
        showModal={showWarningModalDetails.show}
        onClose={() =>
          setShowWarningModalDetails({
            show: false,
            message: "",
          })
        }
        description={
          showWarningModalDetails.message ||
          t("global.globalNotifications.wentWrong")
        }
      />

      {showUploadNew && (
        <ManualDataSourceUploader
          show={showUploadNew}
          setShow={setShowUploadNew}
        />
      )}
    </>
  );
};
